import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../config/api";
import SEO from "../components/SEO";

const translations = {
  tr: {
    backToServices: "Hizmetlere Dön",
    loading: "Yükleniyor...",
    notFound: "Hizmet bulunamadı",
    error: "Hizmet yüklenirken bir hata oluştu"
  },
  en: {
    backToServices: "Back to Services",
    loading: "Loading...",
    notFound: "Service not found",
    error: "Error loading service"
  },
  fr: {
    backToServices: "Retour aux Services",
    loading: "Chargement...",
    notFound: "Service introuvable",
    error: "Erreur lors du chargement"
  }
};

const ServiceDetailPage = ({ lang, isDarkMode, serviceId, onBackClick }) => {
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const currentLang = lang ? lang.toUpperCase() : 'TR';
  const t = translations[lang?.toLowerCase()] || translations.tr;

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId]);

  const fetchService = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/practicearea/${serviceId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        setService(data);
      } else {
        setError(t.notFound);
      }
    } catch (err) {
      setError(t.error);
      console.error('Fetch service error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    }
  };

  const getLocalizedField = (service, field) => {
    if (!service) return '';
    const langField = `${field}${currentLang}`;
    const fallbackField = `${field}TR`;
    return service[langField] || service[fallbackField] || '';
  };

  const detailStyles = `
    .sd-row-centered {
      display: flex !important;
      justify-content: center !important;
    }

    .sd-content-block,
    .sd-content-block p,
    .sd-content-block div,
    .sd-content-block span,
    .sd-content-block li,
    .sd-content-block h1,
    .sd-content-block h2,
    .sd-content-block h3,
    .sd-content-block h4,
    .sd-content-block h5,
    .sd-content-block h6 {
      text-align: center !important;
      color: ${isDarkMode ? '#ccc' : '#444'} !important;
    }
    .sd-content-block h1,
    .sd-content-block h2,
    .sd-content-block h3 {
      color: ${isDarkMode ? '#f0f0f0' : '#333'} !important;
    }
    .sd-content-block ul,
    .sd-content-block ol {
      list-style-position: inside;
      padding-left: 0;
      text-align: center;
    }
  `;

  // Loading state
  if (loading) {
    return (
      <div style={{ paddingTop: '130px', textAlign: 'center', minHeight: '60vh' }}>
        <i className="fa fa-spinner fa-spin" style={{ fontSize: '48px', color: '#1e7a46' }}></i>
        <h2 style={{ marginTop: '20px', color: '#666' }}>{t.loading}</h2>
      </div>
    );
  }

  // Error state
  if (error || !service) {
    return (
      <div style={{ paddingTop: '130px', textAlign: 'center', minHeight: '60vh' }}>
        <i className="fa fa-exclamation-circle" style={{ fontSize: '48px', color: '#dc3545' }}></i>
        <h2 style={{ marginTop: '20px', color: '#666' }}>{error || t.notFound}</h2>
        <button
          onClick={handleBackClick}
          style={{
            marginTop: '20px',
            padding: '12px 30px',
            background: '#1e7a46',
            color: '#fff',
            border: 'none',
            borderRadius: '25px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          <i className="fa fa-arrow-left"></i> {t.backToServices}
        </button>
      </div>
    );
  }

  const title = getLocalizedField(service, 'title');
  const description = getLocalizedField(service, 'description');
  const content = getLocalizedField(service, 'content');

  return (
    <div className="service-detail-page" style={{ paddingTop: '120px', paddingBottom: '40px' }}>
      <style>{detailStyles}</style>

      <SEO
        title={title}
        description={description}
        keywords={`${title}, hukuk hizmeti, legal service`}
        url={`/service/${service.id}`}
        lang={currentLang.toLowerCase()}
        image={service.image || 'img/portfolio/01-small.jpg'}
      />

      {/* Service Header */}
      <div className="service-header" style={{ marginBottom: '30px' }}>
        <div className="container">
          <div className="row sd-row-centered">
            <div className="col-md-10">
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                {service.icon && (
                  <i
                    className={`fa ${service.icon}`}
                    style={{
                      fontSize: '64px',
                      color: '#1e7a46',
                      marginBottom: '20px'
                    }}
                  ></i>
                )}
              </div>
              <h1 style={{
                fontSize: '42px',
                marginBottom: '15px',
                fontWeight: '700',
                textAlign: 'center',
                color: isDarkMode ? '#f0f0f0' : '#333'
              }}>
                {title}
              </h1>
              <p style={{
                fontSize: '20px',
                color: isDarkMode ? '#bbb' : '#666',
                textAlign: 'center'
              }}>
                {description}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Service Image */}
      {service.image && (
        <div className="service-featured-image" style={{ marginBottom: '30px' }}>
          <div className="container">
            <div className="row sd-row-centered">
              <div className="col-md-10">
                <img
                  src={service.image}
                  alt={title}
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '10px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Service Content */}
      <div className="service-content-wrapper" style={{ marginBottom: '30px' }}>
        <div className="container">
          <div className="row sd-row-centered">
            <div className="col-md-8">
              <div
                className="service-content sd-content-block"
                style={{
                  fontSize: '18px',
                  lineHeight: '1.8'
                }}
                dangerouslySetInnerHTML={{ __html: content }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Hizmetlere Dön Butonu */}
      <div style={{ textAlign: 'center', paddingTop: '10px', paddingBottom: '10px' }}>
        <button
          onClick={handleBackClick}
          style={{
            background: 'transparent',
            border: '2px solid #1e7a46',
            padding: '14px 36px',
            borderRadius: '25px',
            cursor: 'pointer',
            color: '#1e7a46',
            fontSize: '16px',
            fontWeight: '600',
            transition: 'all 0.3s ease',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#1e7a46';
            e.currentTarget.style.color = '#fff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = '#1e7a46';
          }}
        >
          <i className="fa fa-arrow-left"></i> {t.backToServices}
        </button>
      </div>
    </div>
  );
};

export default ServiceDetailPage;